import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';

const PrivacyPolicy = () => {
    return (
        <section className="privacy-policy py-5">
            <Container>
                <Row>
                    <Col lg={10} className="mx-auto">
                        <h1 className="mb-4">Privacy Policy</h1>
                        <p className="text-muted mb-5">Last updated: December 2025</p>

                        <div className="policy-content">
                            <h2 className="h3 mb-3">About This Privacy Policy</h2>
                            <p className="mb-4">
                                Nebulyx AI Ltd ("Nebulyx AI", "we", "us", or "our") is committed to respecting and
                                protecting your privacy. This Privacy Policy applies to the Nebulyx AI platform, website,
                                and services (collectively, the "Service"). By using our Service, you agree to the
                                collection and use of information in accordance with this policy.
                            </p>

                            <h2 className="h3 mb-3 mt-5">Information We Collect</h2>

                            <h3 className="h4 mb-3">Information You Provide</h3>
                            <ul className="mb-4">
                                <li>Contact details (name, email, company, job title, phone number)</li>
                                <li>Account credentials (username, password)</li>
                                <li>Company information (industry, size, current processes)</li>
                                <li>Business process data for Digital Twin creation</li>
                                <li>Payment and billing information</li>
                                <li>Communications and support requests</li>
                            </ul>

                            <h3 className="h4 mb-3">Information We Collect Automatically</h3>
                            <ul className="mb-4">
                                <li>Device and connection information (IP address, browser type, operating system)</li>
                                <li>Usage data (features used, pages visited, interaction patterns)</li>
                                <li>Performance data to improve our AI models</li>
                                <li>Cookies and similar technologies</li>
                            </ul>

                            <h2 className="h3 mb-3 mt-5">Cookies and Tracking Technologies</h2>
                            <p className="mb-3">
                                We use cookies and similar tracking technologies to enhance your experience on our website.
                                When you first visit our site, you will be asked to consent to our use of cookies.
                            </p>

                            <h3 className="h4 mb-3">Microsoft Clarity</h3>
                            <p className="mb-3">
                                We use Microsoft Clarity, a user behavior analytics tool, to help us understand how
                                visitors interact with our website. Clarity collects the following data only after
                                you have provided consent:
                            </p>
                            <ul className="mb-4">
                                <li><strong>Session recordings:</strong> Anonymous recordings of your browsing session, including mouse movements, clicks, and scrolling behavior</li>
                                <li><strong>Heatmaps:</strong> Aggregated visualization of where users click, move, and scroll on our pages</li>
                                <li><strong>User interactions:</strong> Button clicks, form interactions, and navigation patterns</li>
                                <li><strong>Device information:</strong> Browser type, screen size, and operating system</li>
                                <li><strong>Page performance:</strong> Load times and errors encountered</li>
                            </ul>
                            <p className="mb-4">
                                Microsoft Clarity does not collect any personal information that directly identifies you
                                (such as your name or email address). All session recordings are anonymized. For more
                                information about how Microsoft processes this data, please visit{' '}
                                <a href="https://privacy.microsoft.com/en-us/privacystatement" target="_blank" rel="noopener noreferrer">
                                    Microsoft's Privacy Statement
                                </a>.
                            </p>

                            <h3 className="h4 mb-3">Your Cookie Choices</h3>
                            <p className="mb-3">
                                When you visit our website, you can choose to:
                            </p>
                            <ul className="mb-4">
                                <li><strong>Accept All:</strong> Enable all cookies and tracking technologies, including Microsoft Clarity</li>
                                <li><strong>Decline:</strong> Reject non-essential cookies. Microsoft Clarity and other analytics tools will not be loaded</li>
                            </ul>
                            <p className="mb-4">
                                You can change your cookie preferences at any time by clearing your browser's cookies
                                and localStorage, then refreshing the page. You will be shown the consent banner again.
                            </p>

                            <h3 className="h4 mb-3">How to Withdraw Consent</h3>
                            <p className="mb-4">
                                To withdraw your consent for analytics tracking:
                            </p>
                            <ol className="mb-4">
                                <li>Clear your browser cookies for nebulyx.ai</li>
                                <li>Clear your browser's localStorage (in Developer Tools → Application → Local Storage)</li>
                                <li>Refresh the page</li>
                                <li>Click "Decline" on the cookie consent banner</li>
                            </ol>

                            <h2 className="h3 mb-3 mt-5">How We Use Your Information</h2>
                            <p className="mb-3">We use your information to:</p>
                            <ul className="mb-4">
                                <li>Provide and manage access to our AI transformation services</li>
                                <li>Create and optimize Digital Twins of your business processes</li>
                                <li>Process transactions and send related information</li>
                                <li>Provide customer support and respond to inquiries</li>
                                <li>Improve our services and develop new features</li>
                                <li>Understand how visitors use our website (via Microsoft Clarity, with consent)</li>
                                <li>Send important updates about our services</li>
                                <li>Comply with legal obligations</li>
                            </ul>

                            <h2 className="h3 mb-3 mt-5">Data Security</h2>
                            <p className="mb-4">
                                We implement appropriate technical and organizational measures to protect your
                                personal information against unauthorized access, alteration, disclosure, or
                                destruction. Your business process data is encrypted both in transit and at rest.
                            </p>

                            <h2 className="h3 mb-3 mt-5">Data Retention</h2>
                            <p className="mb-4">
                                We retain your personal information for as long as necessary to provide our services
                                and fulfill the purposes described in this policy. Digital Twin data is retained
                                according to your subscription agreement. Analytics data collected via Microsoft Clarity
                                is retained according to Microsoft's data retention policies.
                            </p>

                            <h2 className="h3 mb-3 mt-5">Your Rights</h2>
                            <p className="mb-3">Under GDPR and other applicable data protection laws, you have the right to:</p>
                            <ul className="mb-4">
                                <li>Access your personal information</li>
                                <li>Correct inaccurate data</li>
                                <li>Request deletion of your data</li>
                                <li>Object to certain processing</li>
                                <li>Data portability</li>
                                <li>Withdraw consent where applicable (including cookie consent)</li>
                                <li>Lodge a complaint with a supervisory authority</li>
                            </ul>

                            <h2 className="h3 mb-3 mt-5">Third-Party Services</h2>
                            <p className="mb-3">We use the following third-party services that may process your data:</p>
                            <ul className="mb-4">
                                <li><strong>Microsoft Clarity:</strong> Website analytics and session recording (with consent)</li>
                                <li><strong>Cal.com:</strong> Appointment scheduling for discovery sessions</li>
                                <li><strong>YouTube:</strong> Embedded video content</li>
                            </ul>
                            <p className="mb-4">
                                Each third-party service has its own privacy policy governing their use of your data.
                            </p>

                            <h2 className="h3 mb-3 mt-5">Contact Us</h2>
                            <p className="mb-4">
                                For privacy-related questions, to exercise your rights, or to withdraw consent, please contact us at:
                            </p>
                            <p className="mb-1">Email: info@nebulyx.ai</p>
                            <p className="mb-4">Address: Nebulyx AI Ltd, London, United Kingdom</p>

                            <h2 className="h3 mb-3 mt-5">Updates to This Policy</h2>
                            <p>
                                We may update this Privacy Policy from time to time. We will notify you of any
                                material changes by posting the new policy on this page and updating the "Last updated"
                                date.
                            </p>
                        </div>
                    </Col>
                </Row>
            </Container>
        </section>
    );
};

export default PrivacyPolicy;